import { config } from '@/config'
import type {
  AccountCreateResponse,
  AccountOptionsResponse,
  BappebtiFormStatus,
  ClientAccountListResponse,
  ClientCodeResponse,
  ClientRegistrationResponse,
  ClientStatusResponse,
  MoneyTxnListResponse,
  WizardDraftPatch,
  WizardDraftResponse,
} from '@/types/api'

let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

export function getClientId(): string | null {
  return localStorage.getItem(config.storage.clientIdKey)
}

export function setClientId(clientId: string | null) {
  if (clientId) {
    localStorage.setItem(config.storage.clientIdKey, clientId)
  } else {
    localStorage.removeItem(config.storage.clientIdKey)
  }
}

export function clearPersistedAuth() {
  setAccessToken(null)
  localStorage.removeItem('accessToken')
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  params?: Record<string, string | string[] | number | boolean | undefined | null>
  keepalive?: boolean
}

export class ApiError extends Error {
  status: number
  code: string
  detail?: unknown

  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.detail = detail
  }
}

export function formatApiErrorDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg)
        }
        return null
      })
      .filter((msg): msg is string => Boolean(msg))
    if (messages.length > 0) return messages.join('; ')
  }
  return undefined
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth')
    const { configureAmplify, isCognitoConfigured } = await import('@/lib/amplify')
    if (!isCognitoConfigured()) return null
    configureAmplify()
    const session = await fetchAuthSession({ forceRefresh: true })
    const token = session.tokens?.accessToken?.toString()
    if (!token) return null
    setAccessToken(token)
    localStorage.setItem('accessToken', token)
    return token
  } catch {
    return null
  }
}

async function request<T>(endpoint: string, options: RequestOptions & { _authRetried?: boolean } = {}): Promise<T> {
  const { body, params, keepalive, _authRetried = false, ...rest } = options

  let url = `${config.api.baseUrl}${endpoint}`
  if (params) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value))
      }
    }
    const qs = searchParams.toString()
    if (qs) url += `?${qs}`
  }

  const headers: Record<string, string> = {
    ...(rest.headers as Record<string, string>),
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(url, {
    ...rest,
    keepalive,
    headers,
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    if (response.status === 401 && !_authRetried) {
      const refreshed = await refreshAccessToken()
      if (refreshed) {
        return request<T>(endpoint, { ...options, _authRetried: true })
      }
    }

    let errorData: { detail?: unknown; message?: string } = {}
    try {
      errorData = await response.json()
    } catch {
      // ignore
    }

    const message =
      formatApiErrorDetail(errorData.detail) ??
      errorData.message ??
      `Request failed with status ${response.status}`

    throw new ApiError(response.status, `HTTP_${response.status}`, message, errorData.detail)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T>(endpoint: string, params?: RequestOptions['params']) =>
    request<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, body?: unknown, params?: RequestOptions['params']) =>
    request<T>(endpoint, { method: 'POST', body, params }),

  put: <T>(endpoint: string, body?: unknown, params?: RequestOptions['params']) =>
    request<T>(endpoint, { method: 'PUT', body, params }),

  patch: <T>(endpoint: string, body?: unknown, params?: RequestOptions['params'], keepalive?: boolean) =>
    request<T>(endpoint, { method: 'PATCH', body, params, keepalive }),
}

export const clientApi = {
  getStatus: (clientId: string) =>
    api.get<ClientStatusResponse>('/api/v1/client/status', { client_id: clientId }),

  register: (body: unknown) => api.post<ClientRegistrationResponse>('/api/v1/client/register', body),

  confirmEmailOtp: (email: string, code: string) =>
    api.post<{ success: boolean; message: string }>('/api/v1/client/verify-email', { email, code }),

  updateProfile: (clientId: string, body: unknown) =>
    api.put<unknown>('/api/v1/client/profile', body, { client_id: clientId }),

  getCodes: () => api.get<ClientCodeResponse>('/api/v1/client/codes'),

  getAccountOptions: () => api.get<AccountOptionsResponse>('/api/v1/client/accounts/options'),

  getAccounts: (clientId: string) =>
    api.get<ClientAccountListResponse>('/api/v1/client/accounts', { client_id: clientId }),

  createDemoAccount: (clientId: string, body: unknown) =>
    api.post<AccountCreateResponse>('/api/v1/client/accounts/demo', body, { client_id: clientId }),

  createLiveAccount: (clientId: string, body: unknown) =>
    api.post<AccountCreateResponse>('/api/v1/client/accounts/live', body, { client_id: clientId }),

  getBappebtiForm: (clientId: string) =>
    api.get<BappebtiFormStatus>('/api/v1/client/bappebti/form', { client_id: clientId }),

  postConsent: (clientId: string, consentType: string, body: unknown) =>
    api.post<unknown>(`/api/v1/client/bappebti/consent/${consentType}`, body, { client_id: clientId }),

  getTransactions: (clientId: string) =>
    api.get<MoneyTxnListResponse>('/api/v1/client/trading/transactions', { client_id: clientId }),

  getDraft: () => api.get<WizardDraftResponse>('/api/v1/client/draft'),

  patchDraft: (body: WizardDraftPatch, options?: { keepalive?: boolean }) =>
    api.patch<WizardDraftResponse>('/api/v1/client/draft', body, undefined, options?.keepalive),

  submitDraft: () => api.post<WizardDraftResponse>('/api/v1/client/draft/submit', {}),

  getCddsCatalog: () => api.get<CddsCatalog>('/api/v1/client/cdds/catalog'),

  getCddsProgress: (clientId: string) =>
    api.get<CddsProgress>('/api/v1/client/cdds/progress', { client_id: clientId }),

  postCddsYa: (clientId: string, formCode: string, body: { ticks: string[]; extra: Record<string, unknown> }) =>
    api.post(`/api/v1/client/cdds/${formCode}/ya`, body, { client_id: clientId }),

  submitCdds: (clientId: string) =>
    api.post('/api/v1/client/cdds/10/submit', {}, { client_id: clientId }),

  createUploadUrl: (
    clientId: string,
    body: { doc_type: string; filename: string; content_type: string; content_length: number },
  ) =>
    api.post<{ document_id: string; upload_url: string; s3_key: string }>(
      '/api/v1/client/documents/upload-url',
      body,
      { client_id: clientId },
    ),

  confirmUpload: (clientId: string, body: { document_id: string; s3_key: string }) =>
    api.post('/api/v1/client/documents/confirm', body, { client_id: clientId }),

  getDocumentPreviewUrl: (documentId: string) =>
    api.get<{ preview_url: string; expires_in_seconds: number }>(
      `/api/v1/client/documents/${documentId}/preview-url`,
    ),

  companyBanks: () =>
    api.get<Array<{ id: string; bank_name: string; account_number: string; account_name: string }>>(
      '/api/v1/client/funding/company-banks',
    ),

  clientBanks: (clientId: string) =>
    api.get<
      Array<{
        id: string
        bank_name: string
        branch: string
        account_number: string
        account_holder: string
        is_primary: boolean
      }>
    >('/api/v1/client/funding/client-banks', { client_id: clientId }),

  createFundRequest: (clientId: string, body: unknown) =>
    api.post('/api/v1/client/funding/requests', body, { client_id: clientId }),

  fundingRates: () =>
    api.get<{ exchange_rates: number[] }>('/api/v1/client/funding/rates'),
}

export interface CddsForm {
  code: string
  title: string
  kind: string
  paragraphs: Array<string | { id: string; text: string }>
  xor_of?: string[]
  content_version: number
}

export interface CddsCatalog {
  content_version: number
  branches: Array<{ id: string; label: string }>
  required_before_submit: string[]
  forms: CddsForm[]
}

export interface CddsProgress {
  signed: Record<string, string>
  answers?: Record<string, { ticks?: string[]; previous_broker_name?: string; nearest_branch_id?: string }>
  complete: boolean
  missing?: string[]
  conflict?: string[]
}
